import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { UserType } from '../../interfaces/userType.enum';
import { LedgerCloseSettingsUpdate } from '../../interfaces/accounting.interface';
import { LedgerCloseSettingsService } from '../../services/ledger-close-settings.service';
import { useToast } from '../../context/ToastContext';

const LedgerCloseSettings: React.FC = () => {
    const { user } = useAuth();
    const toast = useToast();

    // Role detection — same pattern as SegmentSettings / Sidebar.tsx
    const userRoles = user && user.roles ? (Array.isArray(user.roles) ? user.roles : [user.roles]) : [];
    const isInstitutionAdmin = userRoles.includes(UserType.InstitutionAdmin);
    const isReadOnly = !isInstitutionAdmin;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // closedThrough is stored as "YYYY-MM-DD" or "" (empty string = no lock)
    const [closedThrough, setClosedThrough] = useState<string>('');

    useEffect(() => {
        const loadSettings = async () => {
            setLoading(true);
            try {
                const data = await LedgerCloseSettingsService.get();
                setClosedThrough(data.closedThrough ?? '');
            } catch (err) {
                toast.showApiError(err, 'Failed to load close-books settings');
            } finally {
                setLoading(false);
            }
        };

        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const dto: LedgerCloseSettingsUpdate = {
                closedThrough: closedThrough || null,
            };
            await LedgerCloseSettingsService.update(dto);
            toast.showSuccess('Close-books setting saved');
        } catch (err) {
            toast.showApiError(err, 'Failed to save close-books setting');
        } finally {
            setSaving(false);
        }
    };

    const handleClear = async () => {
        setSaving(true);
        try {
            await LedgerCloseSettingsService.update({ closedThrough: null });
            setClosedThrough('');
            toast.showSuccess('Posting-period lock cleared');
        } catch (err) {
            toast.showApiError(err, 'Failed to clear posting-period lock');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ padding: '24px', maxWidth: 640 }}>
            <Typography variant="h5" gutterBottom>
                Close Books Through
            </Typography>

            {isReadOnly && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    You have read-only access. Only Institution Admins may change this setting.
                </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Any journal posting dated on or before the closed-through date will be blocked. Leave the
                date blank to remove the lock and allow postings to any open period.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
                Setting a closed-through date is permanent until explicitly changed. After year-end close,
                set this to the last day of the closed fiscal year to prevent retroactive postings.
            </Alert>

            {/* Closed Through Date */}
            <TextField
                label="Closed Through"
                type="date"
                value={closedThrough}
                onChange={(e) => setClosedThrough(e.target.value)}
                disabled={isReadOnly}
                InputLabelProps={{ shrink: true }}
                fullWidth
                margin="normal"
                helperText={
                    closedThrough
                        ? `Posting on or before ${closedThrough} is blocked.`
                        : 'No lock active — all periods are open.'
                }
            />

            {/* Action Buttons */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={isReadOnly || saving}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleClear}
                    disabled={isReadOnly || saving || !closedThrough}
                >
                    Clear Lock
                </Button>
            </Box>
        </Box>
    );
};

export default LedgerCloseSettings;
